/**
 * The ready-made text of the mail that carries an offer to the customer. The language is the
 * mail's own, chosen in the send dialog, not the language of the screen: a Serbian salesperson
 * writes to a foreign buyer in English. Serbian and English come first; another language is one
 * more entry in TEXTS.
 */
export type OfferMailLanguage = "SR" | "EN";

export const OFFER_MAIL_LANGUAGES: OfferMailLanguage[] = ["SR", "EN"];

export type OfferMailInput = {
  offerNumber: string;
  /** Names of the offer's lines, in their order. */
  items: string[];
  /** yyyy-MM-dd, or null when the offer has no end date. */
  validUntil: string | null;
  /** The recipient's first name, when the address belongs to a known contact. */
  recipientName: string | null;
  /** The sender's full name, for the closing. */
  senderName: string | null;
};

type Texts = {
  subject: (n: string) => string;
  greeting: (name: string | null) => string;
  offerFor: (items: string) => string;
  offerOnly: (n: string) => string;
  validUntil: (day: string) => string;
  questions: string;
  closing: string;
  and: string;
  date: (iso: string) => string;
};

const EN_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const TEXTS: Record<OfferMailLanguage, Texts> = {
  SR: {
    subject: (n) => `Ponuda ${n}`,
    greeting: (name) => (name ? `Poštovani ${name},` : "Poštovani,"),
    offerFor: (items) => `U prilogu mejla nalazi se ponuda za ${items}.`,
    offerOnly: (n) => `U prilogu mejla nalazi se ponuda ${n}.`,
    validUntil: (day) => `Ponuda važi do ${day}`,
    questions: "U slučaju da imate dodatna pitanja, stojim Vam na raspolaganju.",
    closing: "Srdačan pozdrav,",
    and: "i",
    date: (iso) => {
      const [y, m, d] = iso.split("-");
      return `${d}.${m}.${y}.`;
    },
  },
  EN: {
    subject: (n) => `Offer ${n}`,
    greeting: (name) => (name ? `Dear ${name},` : "Dear Sir or Madam,"),
    offerFor: (items) => `Please find attached our offer for ${items}.`,
    offerOnly: (n) => `Please find attached our offer ${n}.`,
    validUntil: (day) => `The offer is valid until ${day}.`,
    questions: "Should you have any further questions, I remain at your disposal.",
    closing: "Kind regards,",
    and: "and",
    date: (iso) => {
      const [y, m, d] = iso.split("-");
      return `${Number(d)} ${EN_MONTHS[Number(m) - 1] ?? m} ${y}`;
    },
  },
};

/** "a", "a i b", "a, b i c" — each name once, in the offer's order. */
function joinItems(items: string[], and: string): string {
  const names = [...new Set(items.map((i) => i.trim()).filter(Boolean))];
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} ${and} ${names[names.length - 1]}`;
}

export function offerMail(language: OfferMailLanguage, input: OfferMailInput): { subject: string; body: string } {
  const t = TEXTS[language];
  const items = joinItems(input.items, t.and);
  const lines = [t.greeting(input.recipientName), "", items ? t.offerFor(items) : t.offerOnly(input.offerNumber)];
  if (input.validUntil) lines.push(t.validUntil(t.date(input.validUntil)));
  lines.push(t.questions, "", t.closing);
  if (input.senderName) lines.push(input.senderName);
  return { subject: t.subject(input.offerNumber), body: lines.join("\n") };
}

/** The first word of a contact's name: "Marko Marković" greets as "Marko". */
export function firstName(fullName: string | null | undefined): string | null {
  const first = fullName?.trim().split(/\s+/)[0];
  return first ? first : null;
}
